import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import Slider from 'react-slick';
import { FormattedMessage } from 'react-intl';

import store from 'Redux/store';
import useViewport from 'Hooks/useViewport';
import CarouselArrow from 'Components/Carousel/CarouselArrow';
import CarouselPaging from 'Components/Carousel/CarouselPaging';
import FeaturedCarouselSlide from 'Components/Carousel/FeaturedCarouselSlide';
import CarouselCard from 'Components/Carousel/CardPanelCarousel/CarouselCard';

import 'Components/Carousel/CardPanelCarousel/CardPanelCarousel.scss';
import 'Components/Carousel/FeaturedCarousel.scss';

// const FeaturedCarouselSlide = lazy(() => import(/* webpackChunkName: "FeaturedCarouselSlide" */ 'Components/Carousel/FeaturedCarouselSlide'));

const loggedImpressions = [];

export default function CardPanelCarousel({
  slides,
  fantasyVerse,
  title,
  FeaturedTopSellerCarousel,
}) {
  const [interacted, setInteracted] = useState(false);

  const cardPanelCarouselRef = useRef();

  const { isAboveLg } = useViewport();

  const skeleton = isEmpty(slides);

  const logImpression = (index = 0) => {
    const slide = slides[index];
    if (slide && !loggedImpressions.includes(index)) {
      store.dispatch({
        type: 'frontend/carousel/VIEWED',
        payload: { advertId: slide._id, url: slide.url, index, component: 'CardPanelCarousel' },
      });
      loggedImpressions.push(index);
    }
  };

  const logClick = (slide) => {
    const section = fantasyVerse ? 'FantasyVerseCarousel' : 'MainCarousel';
    store.dispatch({
      type: 'frontend/click/CLICK_INTERACTION',
      payload: { section, context: `Slide${slide}` },
    });
  };

  const settings = {
    arrows: true,
    prevArrow: <CarouselArrow ariaLabel="Previous" arrowDirection="left" containerClassName="arrow-btn-container CardPanelCarousel__arrows" />,
    nextArrow: <CarouselArrow ariaLabel="Next" arrowDirection="right" containerClassName="arrow-btn-container CardPanelCarousel__arrows" />,
    infinite: true,
    dots: true,
    dotsClass: 'FeaturedCarousel__pagination',
    slidesToScroll: 1,
    slidesToShow: 1,
    accessibility: true,
    speed: 300,
    fade: true,
    lazyLoad: interacted ? null : 'ondemand',
    onInit: logImpression,
    customPaging: i => (
      <CarouselPaging
        slideIndex={i}
        slideLength={Array.isArray(slides) ? slides.length : 0}
        slidesToShow={1}
      />
    ),
    afterChange: (i) => {
      if (cardPanelCarouselRef && cardPanelCarouselRef.current) {
        cardPanelCarouselRef.current.slickGoTo(i);
      }

      if (!interacted) {
        setInteracted(true);
      }

      logImpression(i);
    },
    beforeChange: (_, next) => {
      logClick(next + 1);
    },
    responsive: [
      {
        breakpoint: 1199,
        settings: {
          fade: true,
        },
      },
      {
        breakpoint: 991,
        settings: {
          fade: false,
        },
      },
      {
        breakpoint: 767,
        settings: {
          fade: false,
          arrows: false,
        },
      },
      {
        breakpoint: 480,
        settings: {
          fade: false,
          arrows: false,
        },
      },
    ],
  };

  if (skeleton) {
    return (
      <React.Fragment>
        <h2 className="CardPanelCarouselHeading">
          {title}

          {!title &&
            <FormattedMessage id="card-panel-carousel.title" defaultMessage="Featured Deals" />
          }
        </h2>

        <div className="CardPanelCarousel CardPanelCarousel--skeleton">
          <div className="CardPanelCarousel__panel">
            <div className="CardPanelCarousel__main">
              <CarouselCard skeleton size={'large'} />
            </div>

            <div className="CardPanelCarousel__support">
              <div className="CardPanelCarousel__support__container">
                <CarouselCard skeleton />
              </div>
              <div className="CardPanelCarousel__support__container">
                <CarouselCard skeleton />
              </div>
              {isAboveLg &&
                <>
                  <div className="CardPanelCarousel__support__container">
                    <CarouselCard skeleton />
                  </div>
                  <div className="CardPanelCarousel__support__container">
                    <CarouselCard skeleton />
                  </div>
                </>
              }
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <h2 className="CardPanelCarouselHeading">
        {title}

        {!title &&
          <FormattedMessage id="card-panel-carousel.title" defaultMessage="Featured Deals" />
        }
      </h2>

      <div className="CardPanelCarousel">
        <Slider
          {...settings}
          className="CardPanelCarousel__content"
          ref={cardPanelCarouselRef}
        >
          {slides.map((card) => {
            if (card[0].promoted && isAboveLg) {
              return (
                <FeaturedCarouselSlide
                  key={Math.random()}
                  slide={card[0]}
                  slideNumber={get(card[0], 'slideNumber', 0)}
                  fantasyVerse={fantasyVerse}
                />
              );
            }

            return (
              <div className="CardPanelCarousel__panel" key={Math.random()}>
                <div className="CardPanelCarousel__main">
                  <CarouselCard
                    size={'large'}
                    card={card[0]}
                    cardNumber={get(card[0], 'slideNumber', 0)}
                    fantasyVerse={fantasyVerse}
                    FeaturedTopSellerCarousel={FeaturedTopSellerCarousel}
                  />
                </div>

                <div className="CardPanelCarousel__support">
                  <div className="CardPanelCarousel__support__container">
                    <CarouselCard
                      card={card[1]}
                      cardNumber={get(card[1], 'slideNumber', 0)}
                      fantasyVerse={fantasyVerse}
                      FeaturedTopSellerCarousel={FeaturedTopSellerCarousel}
                    />
                  </div>
                  <div className="CardPanelCarousel__support__container">
                    <CarouselCard
                      card={card[2]}
                      cardNumber={get(card[2], 'slideNumber', 0)}
                      fantasyVerse={fantasyVerse}
                      FeaturedTopSellerCarousel={FeaturedTopSellerCarousel}
                    />
                  </div>
                  {isAboveLg &&
                    <>
                      <div className="CardPanelCarousel__support__container">
                        <CarouselCard
                          card={card[3]}
                          cardNumber={get(card[3], 'slideNumber', 0)}
                          fantasyVerse={fantasyVerse}
                          FeaturedTopSellerCarousel={FeaturedTopSellerCarousel}
                        />
                      </div>
                      <div className="CardPanelCarousel__support__container">
                        <CarouselCard
                          card={card[4]}
                          cardNumber={get(card[4], 'slideNumber', 0)}
                          fantasyVerse={fantasyVerse}
                          FeaturedTopSellerCarousel={FeaturedTopSellerCarousel}
                        />
                      </div>
                    </>
                  }
                </div>
              </div>
            );
          })}
        </Slider>
      </div>
    </React.Fragment>
  );
}

CardPanelCarousel.propTypes = {
  slides: PropTypes.array,
  fantasyVerse: PropTypes.bool,
  title: PropTypes.string,
  FeaturedTopSellerCarousel: PropTypes.bool,
};

CardPanelCarousel.defaultProps = {
  slides: [],
  fantasyVerse: false,
  title: '',
  FeaturedTopSellerCarousel: false,
};
